<template>
  <v-container fluid class="pa-0 h-100 d-flex flex-column" style="height: 100vh">
    <v-card dark class="flex-grow-1 d-flex flex-column pa-0 m-0 rounded-0" style="height: 100%">
      <v-toolbar color="grey darken-4" flat class="flex-shrink-0">
        <v-btn icon @click="$emit('back')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="ml-2 font-weight-bold"> Einsatzplan {{ monthName }} </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="warning" class="mr-3" @click="confirmRecalculateDialog = true" :loading="recalculating">
          <v-icon left>mdi-calculator-variant</v-icon> Neu berechnen
        </v-btn>
        <v-btn color="primary" @click="refreshData" :loading="loading || recalculating"> <v-icon left>mdi-refresh</v-icon> Aktualisieren </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0 flex-grow-1" style="overflow: hidden">
        <shift-management-roster-view
          v-if="!loading && (assignedPlanData.length > 0 || unassignedShifts.length > 0) && assignedEmployees.length > 0"
          :plan-data="assignedPlanData"
          :studioId="studioId"
          :availableShifts="settings.shifts || []"
          :unassignedShifts="unassignedShifts"
          :employees="assignedEmployees"
          :start-date="startDate"
          :school-holidays="settings.schoolHolidays || []"
          :loading="loading"
          @refresh="refreshData" />
        <div v-else-if="loading" class="d-flex justify-center align-center fill-height flex-column">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <p class="mt-4 grey--text">Lade Planungsdaten...</p>
        </div>
        <div v-else class="text-center pa-5 fill-height d-flex justify-center align-center flex-column">
          <v-icon size="64" color="grey">mdi-calendar-blank</v-icon>
          <p class="mt-4 grey--text">Keine Zuweisungen für diesen Monat gefunden.</p>
          <v-btn color="primary" @click="$emit('back')">Zurück</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="confirmRecalculateDialog" max-width="500px" dark>
      <v-card class="grey darken-3">
        <v-card-title class="warning--text">
          <v-icon left color="warning">mdi-alert-circle</v-icon>
          Monat neu berechnen?
        </v-card-title>
        <v-card-text class="pt-4 white--text">
          Wenn du auf "Ja, berechnen" klickst, passiert Folgendes:
          <ol class="mt-2 mb-2">
            <li>Alle <b>Standard-Schichten</b> in diesem Monat werden geleert (Zuweisungen entfernt).</li>
            <li>Der Auto-Assigner startet <b>sofort neu</b> und teilt die Mitarbeiter frisch ein.</li>
          </ol>
          <br />
          Spezielle Schichten, die du mit dem Grund "Spezial" oder "Fix" angelegt hast, bleiben erhalten.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="confirmRecalculateDialog = false" :disabled="recalculating">Abbrechen</v-btn>
          <v-btn color="warning" @click="executeRecalculation" :loading="recalculating">Ja, neu berechnen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "@/api"
import ShiftManagementRosterView from "./shiftManagementRosterView.vue"

export default {
  name: "ShiftManagementMonthDetail",
  components: {
    ShiftManagementRosterView,
  },
  props: {
    studioId: {
      type: String,
      required: true,
    },
    monthId: {
      type: String, // Format: "YYYY-MM"
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      settings: {},
      allEmployees: [],
      assignedPlanData: [],
      unassignedShifts: [],
      assignedEmployees: [],
      confirmRecalculateDialog: false,
      recalculating: false,
    }
  },
  computed: {
    monthName() {
      return this.$moment(this.monthId, "YYYY-MM").format("MMMM YYYY")
    },
    startDate() {
      return this.$moment(this.monthId, "YYYY-MM").startOf("month").format("YYYY-MM-DD")
    },
    endDate() {
      return this.$moment(this.monthId, "YYYY-MM").endOf("month").format("YYYY-MM-DD")
    },
  },
  async created() {
    await this.initData()
  },
  watch: {
    monthId() {
      this.initData()
    },
  },
  methods: {
    async initData() {
      this.loading = true
      try {
        const [settingsRes, employeesRes] = await Promise.all([
          api.shiftManagement.fetchSettings("local"),
          api.shiftManagement.fetchShiftManagementEmployee(),
        ])
        this.settings = settingsRes
        this.allEmployees = employeesRes
        await this.refreshData()
      } catch (err) {
        console.error("Fehler beim Initialisieren des Monats", err)
        this.$toast.error("Konnte Basisdaten nicht laden.")
      } finally {
        this.loading = false
      }
    },
    async refreshData() {
      this.loading = true
      try {
        const response = await api.shiftManagement.fetchPlan(this.studioId, this.startDate, this.endDate)
        const responseData = response && response.data ? response.data : response
        const freshShifts = responseData && responseData.shifts ? responseData.shifts : Array.isArray(responseData) ? responseData : []

        this.assignedPlanData = freshShifts.filter((p) => p.assignedEmployee)
        this.unassignedShifts = freshShifts.filter((p) => !p.assignedEmployee)

        this.prepareEmployees()
      } catch (err) {
        console.error("Fehler beim Laden der Schichtdaten", err)
        this.$toast.error("Konnte Schichtplan nicht laden.")
      } finally {
        this.loading = false
      }
    },
    async executeRecalculation() {
      if (!this.studioId || !this.startDate || !this.endDate) return

      this.recalculating = true
      try {
        // 1. Reset
        await api.shiftManagement.resetPlanAssignments(this.studioId, this.startDate, this.endDate)

        // 2. Erstelle Setting-Objekt für Assign-Route
        const planningSettings = {}
        const employeeIds = []

        this.assignedEmployees.forEach((emp) => {
          employeeIds.push(emp._id)
          planningSettings[emp._id] = emp.planningSettings
        })

        if (employeeIds.length === 0) {
          this.$toast.warning("Keine Mitarbeiter für die Zuweisung gefunden.")
          this.confirmRecalculateDialog = false
          return
        }

        // 3. Neu Zuweisen
        await api.shiftManagement.assignEmployeesToPlan(this.studioId, this.startDate, this.endDate, planningSettings, employeeIds)

        this.$toast.success("Einsatzplan erfolgreich neu berechnet!")
        this.confirmRecalculateDialog = false

        // 4. Frische Daten laden
        await this.refreshData()
      } catch (err) {
        console.error("Fehler bei Neuberechnung:", err)
        this.$toast.error("Fehler bei der Neuberechnung.")
      } finally {
        this.recalculating = false
      }
    },
    prepareEmployees() {
      const assignedUserIds = [...new Set(this.assignedPlanData.map((p) => String(p.assignedEmployee._id || p.assignedEmployee)))]

      const sourceEmployees = this.allEmployees.filter((emp) => emp.studio && emp.studio.some((s) => String(s._id) === String(this.studioId)))

      this.assignedEmployees = assignedUserIds
        .map((userId) => {
          const employeeData = sourceEmployees.find((e) => String(e.user._id || e.user) === userId)
          if (!employeeData) return null

          const settingsKey = `planningSettings_${employeeData._id}_${this.studioId}`
          const savedSettings = JSON.parse(localStorage.getItem(settingsKey)) || {}
          const planningSettings = {
            percentages: savedSettings.percentages || employeeData.planningSettings?.percentages || {},
            recurringAbsence: savedSettings.recurringAbsence || employeeData.planningSettings?.recurringAbsence || [],
            isReadyForPlanning: savedSettings.isReadyForPlanning || employeeData.planningSettings?.isReadyForPlanning || false,
          }

          return {
            ...employeeData,
            _id: userId,
            sollStunden: this.calculateSollStunden(employeeData, planningSettings),
            plannedHours: this.calculatePlannedHours(userId, this.assignedPlanData) + this.calculatePaidVacationHours(employeeData, planningSettings),
            planningSettings,
          }
        })
        .filter((e) => e !== null)
    },
    calculateSollStunden(employeeData, planningSettings) {
      if (!employeeData) return 0
      const start = this.$moment(this.startDate)
      const end = this.$moment(this.endDate)
      let businessDays = 0
      let current = start.clone()

      const studioInfo = employeeData.studio.find((s) => s._id === this.studioId)
      if (!studioInfo) return 0

      const percentage = this.getPlanningPercentage(employeeData, studioInfo, planningSettings)

      while (current.isSameOrBefore(end, "day")) {
        const dayOfWeekISO = current.isoWeekday()
        const isWeekend = dayOfWeekISO >= 6
        const isSchoolHoliday = (this.settings.schoolHolidays || []).some(
          (sh) => sh.startDate && sh.endDate && current.isBetween(sh.startDate, sh.endDate, null, "[]"),
        )

        let isSchoolDay = false
        if (employeeData.isApprentice && !isSchoolHoliday) {
          isSchoolDay = (employeeData.schoolDays || []).some((sd) => sd.dayOfWeek === dayOfWeekISO)
        }

        let isWorkDay = !isWeekend && !isSchoolDay
        if (employeeData.isApprentice && isSchoolHoliday) {
          isWorkDay = false
        }

        if (isWorkDay) businessDays++
        current.add(1, "day")
      }
      return businessDays * (8.4 * (percentage / 100))
    },
    calculatePlannedHours(userId, planData) {
      let totalHours = 0
      planData.forEach((plan) => {
        const planUserId = String(plan.assignedEmployee._id || plan.assignedEmployee)
        if (planUserId === String(userId)) {
          const timesToUse = plan.times && plan.times.length > 0 ? plan.times : plan.shiftTemplate ? plan.shiftTemplate.times : []
          if (timesToUse) {
            timesToUse.forEach((time) => {
              if (time.type === "Arbeitszeit" && time.start && time.end) {
                const startTime = this.$moment(time.start, "HH:mm")
                const endTime = this.$moment(time.end, "HH:mm")
                if (endTime.isBefore(startTime)) endTime.add(1, "day")
                totalHours += endTime.diff(startTime, "hours", true)
              }
            })
          }
        }
      })
      return totalHours
    },
    calculatePaidVacationHours(employeeData, planningSettings) {
      const start = this.$moment(this.startDate)
      const end = this.$moment(this.endDate)
      const studioInfo = employeeData.studio.find((s) => s._id === this.studioId)
      if (!studioInfo) return 0

      const percentage = this.getPlanningPercentage(employeeData, studioInfo, planningSettings)
      const dailyHours = 8.4 * (percentage / 100)
      let vacationHours = 0

      const vacations = employeeData.vacations || []
      vacations.forEach((vac) => {
        ;(vac.dates || []).forEach((d) => {
          const dateMoment = this.$moment(d.date)
          if (dateMoment.isBetween(start, end, null, "[]")) {
            if (dateMoment.isoWeekday() <= 5) {
              vacationHours += dailyHours
            }
          }
        })
      })
      return vacationHours
    },
    getPlanningPercentage(employee, studioInfo, planningSettings) {
      if (!studioInfo && !employee.timeTrackerPercentage) return 0
      const planningPercentages = planningSettings?.percentages
      if (planningPercentages && planningPercentages[studioInfo._id] != null) {
        return planningPercentages[studioInfo._id]
      }
      if (employee.timeTrackerPercentage != null) return employee.timeTrackerPercentage
      if (studioInfo) return studioInfo.percentage
      return 0
    },
  },
}
</script>
